import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Loader2, ArrowLeft, MailCheck, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request"); // request | reset
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      if (!data.registered) {
        toast.error("No account is registered with that email.");
      } else if (data.google_only) {
        toast.info("This account uses Google sign-in. Please use \u201cContinue with Google\u201d to log in.");
      } else if (data.sent) {
        toast.success("We emailed you a 6-digit reset code.");
        setStep("reset");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim(), code: code.trim(), new_password: newPassword });
      toast.success("Password updated. You can now sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-[#0B0F19] p-12 text-white lg:flex grid-bg">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold">
          <Shield className="h-6 w-6 text-primary" /> IShareLeads
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Locked out? Let's get you back in.</h2>
          <p className="mt-4 max-w-md text-slate-400">Recover your account with a secure one-time code sent to your email.</p>
        </div>
        <p className="font-mono text-xs text-slate-600">© 2026 IShareLeads</p>
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-24">
        <Link to="/login" className="mb-8 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-login-link">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        {step === "request" ? (
          <>
            <h1 className="font-heading text-3xl font-bold">Forgot your password?</h1>
            <p className="mt-2 text-sm text-muted-foreground">Enter your account email and we'll send a 6-digit reset code.</p>
            <form onSubmit={requestCode} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" type="email" required value={email} data-testid="forgot-email-input"
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@agency.com" className="mt-1.5" />
              </div>
              <Button type="submit" disabled={loading} data-testid="send-code-button" className="w-full">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MailCheck className="mr-2 h-4 w-4" />} Send reset code
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold">Enter your code</h1>
            <p className="mt-2 text-sm text-muted-foreground">We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>. Enter it below with your new password.</p>
            <form onSubmit={resetPassword} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="fp-code">6-digit code</Label>
                <Input id="fp-code" required value={code} inputMode="numeric" maxLength={6} data-testid="reset-code-input"
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} placeholder="123456"
                  className="mt-1.5 tracking-[0.5em] font-mono" />
              </div>
              <div>
                <Label htmlFor="fp-newpass">New password</Label>
                <Input id="fp-newpass" type="password" required value={newPassword} data-testid="new-password-input"
                  onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-password-button" className="w-full">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />} Reset password
              </Button>
              <button type="button" onClick={() => setStep("request")} data-testid="resend-code-link"
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground">
                Didn't get a code? Send again
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
